import * as React from 'react';
import { LoadingIndicator } from '@emorg-prd/standard-react';
import {Table, Toolbar, ToolbarItem, Button, ButtonGroup, Icon, Fieldset} from '../../common';
import DoNotDecryptRule from '../../../models/entities/DoNotDecryptRule';
import { RulesFormProps } from './dndrRule/AbstractRulesForm';
import RuleModalSubToInternet from './dndrRule/RuleModalSubToInternet';
import RuleModalSubToOnPrem from './dndrRule/RuleModalSubToOnPrem';
import { DoNotDecryptRequestsDirection } from '../../../models/enums/DNDR/DoNotDecryptRequestsDirection';
import CSVGeneratorButton from '../../common/CSVGeneratorButton';
import BulkUploadModal from './dndrRule/BulkUploadModal';
import Paginator from '../../common/tables/Paginator';
import { DNDRColumn, RuleColumnsLayout } from './RuleColumnsLayout';
import ImplementedRulesModal from './ImplementedRulesModal';
import { VirtualNetwork } from '../../../models/entities/AzureResources/VirtualNetwork';
import DNDROperationBadge from './DNDROperationBadge';
import TrashcanModal from './TrashcanModal';
import { RuleOperation } from '../../../models/enums/DNDR/RuleOperation';
import Select, { StylesConfig } from "react-select";
import './RulesTable.css';
import { WorkflowState } from '../../../models/enums/WorkflowState';


interface RulesTableProps {
	subscriptionRequestName?: string;
	rules: DoNotDecryptRule[];
	implementedRules?: DoNotDecryptRule[];
	validVirtualNetworks?: VirtualNetwork[];
	dndrDirection: DoNotDecryptRequestsDirection;
	readonly?: boolean;
	onChange: (rules: DoNotDecryptRule[]) => any;
	loadingImplementedRules?: boolean;
	failedImplementedRules?: boolean;
	selectedFromExisting?: string[];
	setSelectedFromExisting?: (rule: string[]) => any;
	fromDetails?: boolean;
	currentWorkflowState?: number;
	subscriptionRequestId?: number;
}

interface RulesTableState {
	columns: DNDRColumn[];
	ruleModalVisible: boolean;
	editingRule?: DoNotDecryptRule;
	bulkUploadModalVisible: boolean;
	pageRows: DoNotDecryptRule[];
	implementedRuleModalVisible: boolean;
	optionValueSelected: string,
	showTrashcanModal: boolean;
}

export class RulesTable extends React.Component<RulesTableProps, RulesTableState> {

	constructor(props: RulesTableProps | Readonly<RulesTableProps>) {
		super(props);
		this.state = {
			columns: [],
			ruleModalVisible: false,
			bulkUploadModalVisible: false,
			pageRows: [],
			implementedRuleModalVisible: false,
			showTrashcanModal: false,
			optionValueSelected: "",
		};
	}

	componentDidMount() {
		this.setColumns();
	}

	componentDidUpdate(prevProps: RulesTableProps) {
		if (prevProps.dndrDirection?.value !== this.props.dndrDirection?.value) {
			this.setColumns();
		}
	}

	setColumns() {
		this.setState({
			columns: RuleColumnsLayout.get(this.props.dndrDirection)
		});
	}

	getRulesModalProps(): RulesFormProps {
		let editingRule = null;
		if (this.state.editingRule != null) {
			editingRule = { ...this.state.editingRule };
			if ((editingRule.isTemporary && editingRule.operation == RuleOperation.Modify)) editingRule.operation = RuleOperation.Remove;
			editingRule.name = editingRule.azureName ?? editingRule.name;
		}
		return {
			visible: this.state.ruleModalVisible,
			onConfirm: this.handleRuleChange,
			onClose: () => this.setState({ editingRule: null, ruleModalVisible: false }),
			editingRule: editingRule,
			validVirtualNetworks: this.props.validVirtualNetworks
		};
	}

	handleRuleChange = (rule: DoNotDecryptRule) => {
		let { rules } = this.props;

		if (this.state.editingRule) {
			const index = rules.indexOf(this.state.editingRule);
			rules = [...rules];
			rules[index] = rule;
		} else {
			rules = [...rules, rule];
		}
		this.props.onChange(rules);
		this.setState({ ruleModalVisible: false, editingRule: null });
	};

	handleDeleteAll = () => {
		this.props.onChange([]);
		this.resetTrashcanModal();
	};

	handleDeleteOrRemoveRule = (ruleToRemove: DoNotDecryptRule) => {
		this.setState({ showTrashcanModal: true, editingRule: ruleToRemove })
	};

	handleRemoveRule = () => {
		const rules = this.props.rules.filter(rule => rule !== this.state.editingRule);
		this.props.setSelectedFromExisting(this.props.selectedFromExisting?.filter(ruleName => ruleName !== this.state.editingRule.name));
		this.props.onChange(rules);
		this.resetTrashcanModal();
	};

	handleDeleteRule = () => {
		let editingRule = { ...this.state.editingRule };
		editingRule.operation = RuleOperation.Remove;
		editingRule.name = editingRule.azureName;
		this.handleRuleChange(editingRule);
		this.resetTrashcanModal();
	};


	resetTrashcanModal = () => {
		this.setState({
			editingRule: null,
			showTrashcanModal: false
		})
	}

	closeTrashcanModal = this.resetTrashcanModal;

	handleBulkUpload = (rules: DoNotDecryptRule[]) => {
		this.setState({
			bulkUploadModalVisible: false,
			optionValueSelected: '',
		});
		this.props.onChange([...this.props.rules, ...rules]);
	};

	handlePageChange = (page: DoNotDecryptRule[]) => {
		this.setState({
			pageRows: page
		});
	};

	handleImplementedRulesOnSuccess = (impRules: DoNotDecryptRule[]) => {
		this.setState({
			implementedRuleModalVisible: false,
			optionValueSelected: '',
		});
		this.props.setSelectedFromExisting([...this.props.selectedFromExisting, ...impRules.map(rule => rule.name)])
		this.props.onChange([...this.props.rules, ...impRules]);
	};

	directionIs(direction: DoNotDecryptRequestsDirection) {
		return this.props.dndrDirection === direction;
	}

	getOperationName(rule: DoNotDecryptRule) {
		if (this.props.fromDetails && this.props.currentWorkflowState !== WorkflowState.Review.value) {
			return this.state.columns.find(column => column.header === 'Operation').cell(rule)
		}
		const nameColumn = this.state.columns.find(column => column.header === 'Name')
		const operationColumn = this.state.columns.find(column => column.header === 'Operation')
		return nameColumn.cell(rule) ? operationColumn.cell(rule) : 'to be updated'

	}

	handleOptionSelected = (option) => {
		this.setState({ optionValueSelected: option.value });
		if (option.value === "import-new-rules") {
			this.setState({ bulkUploadModalVisible: true });
		} else {
			if (!option.isDisabled) this.setState({ implementedRuleModalVisible: true });
		}
	}

	CustomOption = (props: { data: any; innerRef: any; innerProps: any; isDisabled: boolean }) => {
		const { data, innerRef, innerProps, isDisabled } = props;
		return (
			<div
				style={{ cursor: `${isDisabled ? 'not-allowed' : 'pointer'}` }}
				ref={innerRef}
				{...innerProps}
				className='custom-option-box'
			>
				<div style={{ color: `${isDisabled ? '#999' : '#1E55B1'}`, fontSize: 'large', }}>{data.isLoading ? data.loadingMessage : data.label}</div>
				<div style={{ color: '#999', fontSize: 'small', wordWrap: 'break-word', width: '180px' }}>{data.subtext}</div>
			</div>
		)
	}

	styles: StylesConfig<any, false> = {
		control: (css) => ({ ...css, width: 200, zIndex: 5 }),
	};

	render() {
		const tableHeaders = this.state.columns?.filter(column => column.header !== "Protocol" && column.header !== "Description").map((column) => (
			<Table.Head.Cell key={column.header}>
				{column.header}
			</Table.Head.Cell>
		));
		const tableRows = [];
		const defaultLimit = 10;
		const options = [
			{
				value: "import-new-rules",
				label: 'Import new rules',
				subtext: 'Add new rules at the same time with CSV file',
				isDisabled: this.props.dndrDirection == null || !(this.props.validVirtualNetworks.length > 0),
				isLoading: this.props.dndrDirection == null || !(this.props.validVirtualNetworks.length > 0),
				loadingMessage: ''
			},
			{
				value: "import-existing-rule",
				label: 'Import existing rules',
				subtext: 'Add existing rules to delete or modify from this subscritpion',
				isDisabled: this.props.loadingImplementedRules || !(this.props.implementedRules?.length > 0),
				isLoading: this.props.loadingImplementedRules || !(this.props.implementedRules?.length > 0),
				loadingMessage: 'Loading Existing Rules...'
			},
		];

		this.state.pageRows.forEach((rule, rowIndex) => {
			tableRows.push(
				<Table.Row key={rowIndex}>
					<Table.Cell>
						{this.props.rules.indexOf(rule) + 1}
					</Table.Cell>
					{this.state.columns?.filter(column => column.header !== "Protocol" && column.header !== "Description").map((column, colIndex) => (
						<Table.Cell
							key={`${rowIndex}-${colIndex}`}
							className="em-u-margin-right-auto"
							title={(column.cell(rule) || '-')}
						>
							{column.header === 'Operation' ? (<DNDROperationBadge>{this.getOperationName(rule)}</DNDROperationBadge>) : (column.cell(rule)?.length > (column.limit || defaultLimit) ?
								(`${column.cell(rule).trim().substring(0, (column.limit || defaultLimit))}...`)
								:
								(column.cell(rule) || '-'))}
						</Table.Cell>
					))}
					<Table.Cell>
						{!this.props.readonly ?
							<ButtonGroup>
								<Button
									style={{ zIndex: 0 }}
									variant="primary"
									size="small"
									onClick={() => this.setState({ ruleModalVisible: true, editingRule: rule })}
								>
									<Icon icon="pencil" size="small" />
								</Button>
								<Button
									style={{ zIndex: 0 }}
									variant="primary"
									size="small"
									onClick={() => this.handleDeleteOrRemoveRule(rule)}
								>
									<Icon icon="trashcan" size="small" />
								</Button>
							</ButtonGroup>
							:
							<Button
								style={{ zIndex: 0 }}
								variant="primary"
								size="small"
								onClick={() => this.setState({ ruleModalVisible: true, editingRule: rule })}
							>
								See details
							</Button>
						}
					</Table.Cell>
				</Table.Row>
			);
		});

		return (
			<>
				<Fieldset legend='Ruleset'>
						<Table
							header={
								<Toolbar>
									{this.props.readonly &&
										<ToolbarItem right>
											<CSVGeneratorButton
												data={this.props.rules}
												columns={this.state.columns}
											/>
										</ToolbarItem>}
									{!this.props.readonly &&
										<ToolbarItem right>
												<ButtonGroup>
													<Button
														variant="primary"
														size="small"
														disabled={!this.props.dndrDirection}
														onClick={() => this.setState({ ruleModalVisible: true })}
													>
														{!(this.props.validVirtualNetworks.length > 0) ?
															<>
																{(this.props.failedImplementedRules) ?
																	<>
																		<LoadingIndicator size="s" />
																		<span>Operation Failed</span>
																	</>
																	:
																	<>
																		<LoadingIndicator size="s" />
																		<span>Loading Virtual Networks...</span>
																	</>}
															</>
															:
															<>
															<Icon icon="plus-filled" size="medium" />
																	<span>Add New Rule</span>
															</>}
													</Button>
													<Select
														placeholder='Import rules'
														value={this.state.optionValueSelected != null ? this.state.optionValueSelected : ''}
														onChange={(option) => this.handleOptionSelected(option)}
														components={{ Option: this.CustomOption }}
														options={options}
														styles={this.styles}
													/>
												</ButtonGroup>
										</ToolbarItem>}
								</Toolbar>
							}
							footer={
								<Paginator
									data={this.props.rules}
									onPageChange={this.handlePageChange}
								/>
							}
						>
							<Table.Head>
								<Table.Head.Row>
									<Table.Head.Cell />
									{tableHeaders}
									<Table.Head.Cell>
										{!this.props.readonly ? 'Actions' : ''}
									</Table.Head.Cell>
								</Table.Head.Row>
							</Table.Head>
							<Table.Body>
								{tableRows.length > 0 ?
									tableRows
									:
									<Table.Row>
										<Table.Cell
											colSpan={this.state.columns?.length + 2 || 2}
										>
											<i>No rules found.</i>
										</Table.Cell>
									</Table.Row>}
							</Table.Body>
						</Table>
				</Fieldset>
				{this.state.showTrashcanModal &&
					<TrashcanModal
						onClose={this.resetTrashcanModal}
						onDelete={this.handleDeleteRule}
						onRemove={this.handleRemoveRule}
					/>
				}

				{this.directionIs(DoNotDecryptRequestsDirection.SubscriptionToInternet) &&
					<RuleModalSubToInternet
						{...this.getRulesModalProps()}
						subscriptionRequestName={this.props.subscriptionRequestName}
						isReadOnly={this.props.readonly}
					/>}
				{this.directionIs(DoNotDecryptRequestsDirection.SubscriptionToOnPrem) &&
					<RuleModalSubToOnPrem
						{...this.getRulesModalProps()}
						subscriptionRequestName={this.props.subscriptionRequestName}
						isReadOnly={this.props.readonly}
					/>}
				{this.state.implementedRuleModalVisible &&
					<ImplementedRulesModal
						direction={this.props.dndrDirection}
						rules={this.props.implementedRules}
						onSuccess={this.handleImplementedRulesOnSuccess}
						visible={this.state.implementedRuleModalVisible}
						alreadySelected={this.props.selectedFromExisting}

						onCancel={(ev) => {
							this.setState({
								implementedRuleModalVisible: false,
								optionValueSelected: '',
							});
						}}
					/>}
				<BulkUploadModal
					subscriptionRequestId={this.props.subscriptionRequestId}
					visible={this.state.bulkUploadModalVisible}
					direction={this.props.dndrDirection}
					onSuccess={this.handleBulkUpload}
					onCancel={(ev) => {
						this.setState({
							bulkUploadModalVisible: false,
							optionValueSelected: '',
						});
					}}
				/>
			</>
		);
	}
}